import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CopyButton,
  DescriptionList,
  Dropdown,
  EmptyState,
  IconButton,
  Identity,
  Inline,
  LinkButton,
  Modal,
  Pagination,
  SearchInput,
  SegmentedControl,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableLoading,
  TableRow,
  Text,
  Toolbar,
  ToolbarCount,
  ToolbarItem,
  ToolbarSpacer,
  useToast,
  VisuallyHidden,
  type BadgeTone,
} from '@hkdevs/design-system';
import { useState } from 'react';
import { useMe } from '../../api/queries/me';
import { useEnabledRoles } from '../../api/queries/roles';
import { offersRole } from '../../auth/roles';
import {
  useAdminUsers,
  useCreateUser,
  useExternalIdTypes,
  useSupervisors,
  useUpdateUser,
  type AdminUser,
  type Role,
} from '../../api/queries/users';
import {
  formErrors,
  ROLE_LABELS,
  toUserBody,
  UserModal,
  userValues,
  type UserFormErrors,
  type UserFormValues,
} from './UserModal';
import { withReference } from '../../api/withReference';
import { RouterLink } from '../../shell/RouterLink';

export const PAGE_SIZE = 25;

/**
 * The role filter (B12: the API's `role`, where rep includes users with no role; rep intern
 * and pay viewer are exact, ADR 0028).
 */
export type RoleFilter = Role;

/** The Users page's URL state (ADR 0023): the search, the role and the page. */
export interface UsersSearch {
  q?: string | undefined;
  role?: RoleFilter | undefined;
  /** 1-based; left out for the first. */
  page?: number | undefined;
}

const ROLE_FILTERS: { value: 'all' | RoleFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'admin', label: 'Admin' },
  { value: 'supervisor', label: 'Supervisor' },
  { value: 'rep', label: 'Rep' },
  { value: 'rep_intern', label: 'Rep (intern)' },
  { value: 'pay_viewer', label: 'Pay viewer' },
];

export function parseUsersSearch(search: Record<string, unknown>): UsersSearch {
  const page = Number(search.page);
  const role = ROLE_FILTERS.find((f) => f.value !== 'all' && f.value === search.role);
  return {
    q: typeof search.q === 'string' && search.q ? search.q : undefined,
    role: role ? (role.value as RoleFilter) : undefined,
    page: Number.isInteger(page) && page > 1 ? page : undefined,
  };
}

export const ROLE_TONES: Record<Role, BadgeTone> = {
  admin: 'accent',
  operator: 'accent',
  supervisor: 'primary',
  rep: 'neutral',
  rep_intern: 'neutral',
  pay_viewer: 'primary',
};

/**
 * Who "View as" can open: anyone but an admin, an operator and yourself, pay viewers
 * included (the API's admin target rule, ADR 0017's A20 and A25 amendments).
 */
const canViewAs = (user: { id: string; roles: readonly Role[] }, meId: string | undefined) =>
  user.id !== meId && !user.roles.includes('admin') && !user.roles.includes('operator');

const listOrDash = (items: readonly string[]) => (items.length ? items.join(', ') : '—');
const plural = (count: number) =>
  `${count.toLocaleString('en-US')} ${count === 1 ? 'user' : 'users'}`;

/** Who the user modal is open for: a new user, or one being edited. */
type Editing = { mode: 'create' } | { mode: 'edit'; user: AdminUser };

export interface UsersViewProps {
  search: UsersSearch;
  onSearchChange: (next: UsersSearch) => void;
  /** The Create user modal is open (the page header's action opens it). */
  creating: boolean;
  onCreatingChange: (creating: boolean) => void;
}

/** The admin's Users page body (pc-dbb.6). */
export function UsersView({ search, onSearchChange, creating, onCreatingChange }: UsersViewProps) {
  const { toast } = useToast();
  const { data: me } = useMe();
  const { data: enabledRoles } = useEnabledRoles();
  // Switched-off roles aren't offered as filters (ADR 0028).
  const roleFilters = ROLE_FILTERS.filter(
    (f) => f.value === 'all' || offersRole(enabledRoles, f.value),
  );
  const role = search.role && offersRole(enabledRoles, search.role) ? search.role : undefined;
  const page = search.page ?? 1;
  const q = search.q?.trim() || undefined;
  const users = useAdminUsers({ q, role, page, pageSize: PAGE_SIZE });
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [editingUser, setEditingUser] = useState<AdminUser | null>(null);
  const [confirming, setConfirming] = useState<AdminUser | null>(null);
  const editing: Editing | null = editingUser
    ? { mode: 'edit', user: editingUser }
    : creating
      ? { mode: 'create' }
      : null;

  /** New filters go back to the first page. */
  const set = (patch: Partial<UsersSearch>) =>
    onSearchChange({ ...search, ...patch, page: undefined });
  const clearAll = () => onSearchChange({});

  const update = useUpdateUser();
  const setEnabled = (user: AdminUser, enabled: boolean) => {
    setConfirming(null);
    update.mutate(
      { id: user.id, body: { enabled } },
      {
        onSuccess: () =>
          toast({
            tone: 'success',
            title: enabled ? `${user.name} is enabled` : `${user.name} is disabled`,
          }),
        onError: (error) =>
          toast(
            withReference(
              {
                tone: 'danger',
                title: enabled ? `Couldn’t enable ${user.name}` : `Couldn’t disable ${user.name}`,
                description: formErrors(error, userValues(user))?.form ?? 'Try again.',
              },
              error,
            ),
          ),
      },
    );
  };

  let body;
  if (users.isError) {
    body = (
      <Alert
        tone="danger"
        title="Users couldn’t be loaded"
        action={
          <Button size="sm" variant="secondary" onClick={() => void users.refetch()}>
            Retry
          </Button>
        }
      >
        Check your connection and try again.
      </Alert>
    );
  } else if (users.data && users.data.total === 0) {
    body = (
      <Card>
        <CardBody>
          {q || role ? (
            <EmptyState
              title="No matching users"
              description="No users match your search or filter. Try a different name, email, or role."
              icon="search"
              headingLevel={2}
              actions={
                <Button size="sm" variant="secondary" onClick={clearAll}>
                  Clear filters
                </Button>
              }
            />
          ) : (
            <EmptyState
              title="No users yet"
              description="Create a user, or import your org structure."
              icon="users"
              headingLevel={2}
            />
          )}
        </CardBody>
      </Card>
    );
  } else {
    const data = users.data;
    const pageCount = data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1;
    body = (
      <>
        <Card flush>
          <Table aria-label="Users" responsive="cards" busy={!data || users.isFetching}>
            <TableHead>
              <TableCell>User</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Roles</TableCell>
              <TableCell>Supervisors</TableCell>
              <TableCell>
                <VisuallyHidden>Actions</VisuallyHidden>
              </TableCell>
              <TableCell width="44px">
                <VisuallyHidden>Details</VisuallyHidden>
              </TableCell>
            </TableHead>
            <TableBody>
              {!data ? (
                <TableLoading colSpan={6} rows={6} label="Loading users" />
              ) : (
                data.items.map((user) => {
                  const expanded = expandedId === user.id;
                  return (
                    <TableRow
                      key={user.id}
                      label={`Details for ${user.name}`}
                      expanded={expanded}
                      onToggle={() => setExpandedId(expanded ? null : user.id)}
                      detail={<UserDetails user={user} onClose={() => setExpandedId(null)} />}
                    >
                      <TableCell rowHeader>
                        <Identity
                          name={user.name}
                          size="sm"
                          {...(user.pictureUrl && { avatarSrc: user.pictureUrl })}
                          {...(!user.enabled && {
                            secondary: (
                              <Badge tone="danger" size="sm">
                                Disabled
                              </Badge>
                            ),
                          })}
                        />
                      </TableCell>
                      <TableCell label="Email" tone="muted">
                        {user.email}
                      </TableCell>
                      <TableCell label="Roles">
                        <Inline as="span" gap={1} align="stretch">
                          {user.roles.map((role) => (
                            <Badge key={role} tone={ROLE_TONES[role]} size="sm">
                              {ROLE_LABELS[role]}
                            </Badge>
                          ))}
                        </Inline>
                      </TableCell>
                      <TableCell label="Supervisors" tone="muted">
                        {listOrDash(user.supervisors.map((supervisor) => supervisor.name))}
                      </TableCell>
                      <TableCell>
                        <Inline as="span" gap={2} justify="end" wrap={false}>
                          {canViewAs(user, me?.user.id) && (
                            // A link: the browser opens it here or in a new tab, and this
                            // tab stays the admin's (ADR 0017, A19).
                            <LinkButton
                              as={RouterLink}
                              href={`/view-as/${user.id}`}
                              aria-label={`View as ${user.name}`}
                            >
                              View as
                            </LinkButton>
                          )}
                          <Dropdown
                            align="end"
                            trigger={
                              <IconButton
                                icon="more-vertical"
                                size="sm"
                                variant="ghost"
                                aria-label={`Actions for ${user.name}`}
                              />
                            }
                            items={[
                              { label: 'Edit user', onClick: () => setEditingUser(user) },
                              user.enabled
                                ? {
                                    label: 'Disable user',
                                    danger: true,
                                    onClick: () => setConfirming(user),
                                  }
                                : { label: 'Enable user', onClick: () => setConfirming(user) },
                            ]}
                          />
                        </Inline>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </Card>
        {data && pageCount > 1 && (
          <Pagination
            page={page}
            pageCount={pageCount}
            onPageChange={(next) =>
              onSearchChange({ ...search, page: next > 1 ? next : undefined })
            }
            size="sm"
          />
        )}
      </>
    );
  }

  return (
    <Stack gap={3} shrink>
      <Toolbar aria-label="Filter users">
        <ToolbarItem size="search">
          <SearchInput
            label="Search users by name or email"
            placeholder="Search by name or email"
            value={search.q ?? ''}
            onValueChange={(next) => set({ q: next || undefined })}
          />
        </ToolbarItem>
        <SegmentedControl
          aria-label="Role"
          size="sm"
          options={roleFilters}
          value={role ?? 'all'}
          onChange={(next) => set({ role: next === 'all' ? undefined : (next as RoleFilter) })}
        />
        <ToolbarSpacer />
        {users.data && <ToolbarCount>{plural(users.data.total)}</ToolbarCount>}
      </Toolbar>
      {body}

      <Modal
        open={!!confirming}
        onClose={() => setConfirming(null)}
        size="sm"
        title={confirming && `${confirming.enabled ? 'Disable' : 'Enable'} ${confirming.name}?`}
        description={
          confirming?.enabled
            ? 'They won’t be able to sign in until you enable them again. Their pay history is kept.'
            : 'They’ll be able to sign in again.'
        }
        footer={
          confirming && (
            <>
              <Button variant="secondary" onClick={() => setConfirming(null)}>
                Cancel
              </Button>
              <Button
                variant={confirming.enabled ? 'danger' : 'primary'}
                onClick={() => setEnabled(confirming, !confirming.enabled)}
              >
                {confirming.enabled ? 'Disable user' : 'Enable user'}
              </Button>
            </>
          )
        }
      />

      {editing && (
        <UserEditor
          key={editing.mode === 'edit' ? editing.user.id : 'new'}
          editing={editing}
          onClose={() => {
            setEditingUser(null);
            onCreatingChange(false);
          }}
        />
      )}
    </Stack>
  );
}

/** The user modal with its options, saving through the API. */
function UserEditor({ editing, onClose }: { editing: Editing; onClose: () => void }) {
  const { toast } = useToast();
  const supervisors = useSupervisors();
  const types = useExternalIdTypes();
  const create = useCreateUser();
  const update = useUpdateUser();
  const [serverErrors, setServerErrors] = useState<UserFormErrors>();
  const editedId = editing.mode === 'edit' ? editing.user.id : undefined;

  const save = (values: UserFormValues) => {
    setServerErrors(undefined);
    const body = toUserBody(values);
    const onError = (error: unknown) => {
      const errors = formErrors(error, values);
      if (errors) setServerErrors(errors);
      else
        toast(
          withReference(
            {
              tone: 'danger',
              title: 'The user couldn’t be saved',
              description: 'Check your connection and try again.',
            },
            error,
          ),
        );
    };
    if (editedId) {
      update.mutate(
        { id: editedId, body: { ...body, enabled: values.enabled } },
        {
          onSuccess: (user) => {
            toast({ tone: 'success', title: `${user.name} is saved` });
            onClose();
          },
          onError,
        },
      );
    } else {
      create.mutate(body, {
        onSuccess: (user) => {
          toast({ tone: 'success', title: `${user.name} is created` });
          onClose();
        },
        onError,
      });
    }
  };

  return (
    <UserModal
      open
      onClose={onClose}
      mode={editing.mode}
      {...(editing.mode === 'edit' && {
        initialValues: userValues(editing.user),
        operator: editing.user.roles.includes('operator'),
      })}
      supervisorOptions={(supervisors.data ?? [])
        .filter((user) => user.id !== editedId)
        .map((user) => ({ value: user.id, label: user.name }))}
      externalIdTypes={(types.data ?? []).map((type) => ({ value: type.id, label: type.name }))}
      onSubmit={save}
      submitting={create.isPending || update.isPending}
      serverErrors={serverErrors}
    />
  );
}

/** A user's expanded row: their IDs, roles, supervisors and status. */
function UserDetails({ user, onClose }: { user: AdminUser; onClose: () => void }) {
  return (
    <Stack gap={4}>
      <DescriptionList
        items={[
          {
            term: 'User ID',
            description: (
              <Inline as="span" gap={2}>
                <Text mono>{user.id}</Text>
                <CopyButton value={user.id} label="Copy" />
              </Inline>
            ),
          },
          {
            term: user.externalIds.length > 1 ? 'External IDs' : 'External ID',
            description: user.externalIds.length
              ? user.externalIds.map((id) => (
                  <div key={id.typeId}>
                    {id.typeName}: <Text mono>{id.value}</Text>
                  </div>
                ))
              : '—',
          },
          { term: 'Roles', description: user.roles.map((role) => ROLE_LABELS[role]).join(', ') },
          {
            term: 'Supervisors',
            description: listOrDash(user.supervisors.map((supervisor) => supervisor.name)),
          },
          { term: 'Status', description: user.enabled ? 'Active' : 'Disabled' },
        ]}
      />
      <Inline justify="end">
        <LinkButton onClick={onClose}>Close</LinkButton>
      </Inline>
    </Stack>
  );
}
